import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import intersection from 'lodash/intersection';
import request from '../utils/request';
import { API_URL, TRELLO_KEY } from '../config';

/* eslint-disable react/no-unused-state */

const Code = styled.pre`
  white-space: pre-wrap;
`;

const initialState = {
  board: undefined,
  labels: undefined,
  members: undefined,
  list: undefined,
  error: null,
  loading: false,
  working: false,
};

class Trello extends React.PureComponent {
  state = initialState

  componentDidMount() {
    this.checkLoading();
  }

  componentDidUpdate(prevProps) {
    this.checkLoading(prevProps);
  }

  checkLoading(prevProps) {
    // Reload if token changed
    const { token, board } = this.props.storage;
    const { error } = this.state;
    if (!this.isEqual('token', prevProps)) {
      this.setState(initialState);
      if (token) {
        this.request({ board: { url: 'members/me/boards' } })
          .catch(this.logError);
      }
    }
    if (!this.isEqual('board', prevProps)) {
      if (token && !(error)) {
        this.request({
          list: { url: `boards/${board}/lists` },
          labels: { url: `boards/${board}/labels` },
          members: { url: `boards/${board}/memberships`, query: { member: true } },
        })
          .catch(this.logError);
      }
    }
  }

  isEqual = this.isEqual.bind(this);

  isEqual(key, prevProps) {
    if (!prevProps) {
      return false;
    }
    return this.props.storage[key] === prevProps.storage[key];
  }

  logError = this.logError.bind(this);

  logError(err) {
    err.response.text().then((body) => {
      const response = {
        body,
        url: err.response.url,
      };
      const error = <React.Fragment><h4>{err.toString()}</h4><Code>{JSON.stringify(response, null, 2)}</Code></React.Fragment>;
      this.setState({ error, loading: false, working: false });
    });
  }

  requestPromise = this.requestPromise.bind(this);

  requestPromise(options, key = 'loading') {
    const { token } = this.props.storage;
    const query = { ...options.query, key: TRELLO_KEY, token };
    const url = `${API_URL}${options.url}`;
    this.setState({ [key]: true });
    return request({ ...options, url, query });
  }

  request = this.request.bind(this);

  request(keyUrls) {
    const keys = Object.keys(keyUrls);
    const promises = Object.values(keyUrls).map(this.requestPromise);
    return Promise.all(promises)
      .then((data) => {
        const state = keys.reduce((acc, key, idx) => ({
          ...acc,
          [key]: data[idx].map(keyPick(key)).filter((item) => item.name),
        }), { loading: false });
        this.setState(state);
      });
  }

  filterList = this.filterList.bind(this);

  filterList(list, key) {
    const options = this.state[key].map((item) => item.id);
    return intersection(list, options);
  }

  handleSubmit = this.handleSubmit.bind(this);

  handleSubmit(data) {
    const { storage, onUpdate } = this.props;
    const {
      name, desc, labels, members,
    } = data;
    onUpdate({ labels, members });

    this.requestPromise({
      url: 'cards',
      method: 'POST',
      query: {
        name,
        desc,
        idList: storage.list,
        idLabels: this.filterList(labels, 'labels').join(','),
        idMembers: this.filterList(members, 'members').join(','),
      },
    }, 'working').then((result) => {
      this.alert('Card submitted');
      const newCard = pick(result, ['id', 'name', 'shortUrl', 'dateLastActivity']);
      const oldCards = storage.cards || [];
      onUpdate({ cards: [newCard, ...oldCards].slice(0, 5) });
      this.setState({ working: false });
    })
      .catch(this.logError);
  }

  dismiss = this.dismiss.bind(this);

  dismiss(key) {
    return () => {
      switch (key) {
        case 'alert':
          this.alert(null);
          break;
        case 'error':
          this.setState({ error: null });
          break;
        default:
          // console.error('Can not dismiss', key);
      }
    };
  }

  clone = this.clone.bind(this);

  clone(elem) {
    const { storage, onUpdate } = this.props;
    const optionProperties = ['board', 'list', 'labels', 'members'];
    const attribs = {
      storage,
      onUpdate,
      onSubmit: this.handleSubmit,
      dismiss: this.dismiss,
      options: pick(this.state, optionProperties),
      status: omit(this.state, optionProperties),
    };
    return React.cloneElement(elem, attribs);
  }

  alert = this.alert.bind(this);
  timer = null;

  alert(message, delay = 3000) {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.setState({ alert: message });
    if (message) {
      this.timer = setTimeout(() => this.setState({ alert: null }), delay);
    }
  }

  render() {
    return React.Children.map(this.props.children, this.clone);
  }
}

Trello.propTypes = {
  children: PropTypes.node,
  storage: PropTypes.object,
  onUpdate: PropTypes.func,
};

export default Trello;

function keyPick(key) {
  return (item) => {
    switch (key) {
      case 'labels':
        return pick(item, ['id', 'name', 'color']);
      case 'members':
        return { id: item.member.id, name: item.member.fullName };
      default:
        return pick(item, ['id', 'name']);
    }
  };
}



// WEBPACK FOOTER //
// ./src/components/Trello.js